import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    AlertTriangle,
    MessageSquare,
    ShieldAlert,
    ArrowRight,
    Check,
    X,
    Scale,
    AlertOctagon,
    TrendingDown,
    Building,
    HelpCircle
} from 'lucide-react';
import PageHero from '../components/layout/PageHero';
import SEO from '../components/SEO';
import RichTextRenderer from '../components/content/RichTextRenderer';
import critiqueData from '../data/critique_content.json';
import socialismData from '../data/not_socialism_content.json';
import './MythVsReality.css';
import '../pages/Critique.css';
import '../pages/NotSocialism.css';

export default function MythVsReality() {
    const [activeTab, setActiveTab] = useState('critiques');

    // Scroll to top on tab change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeTab]);

    const tabs = [
        { id: 'critiques', label: 'Common Critiques', icon: ShieldAlert },
        { id: 'imitations', label: 'The Imitations', icon: AlertTriangle },
        { id: 'socialism', label: 'Socialism FAQ', icon: Building }
    ];

    const renderCritiques = () => (
        <div className="tab-content critiques-tab">
            <div className="critique-intro">
                <p>{critiqueData.critique_page.intro}</p>
            </div>
            <div className="imitations-grid">
                {critiqueData.critiques.map((item, index) => (
                    <motion.div
                        key={item.id}
                        className="imitation-card glass-panel"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        viewport={{ once: true }}
                    >
                        <div className="imitation-img-wrapper">
                            <img src={item.image} alt={item.header} />
                            <div className="imitation-badge" style={{ background: 'var(--accent-blue)' }}>
                                {item.category}
                            </div>
                        </div>
                        <div className="imitation-content">
                            <h3>{item.header}</h3>
                            <div className="imitation-comparison">
                                <div className="comp-block fear">
                                    <strong>The Fear:</strong>
                                    <RichTextRenderer content={item.text} />
                                </div>
                                <div className="comp-block reality">
                                    <strong>The Reality:</strong>
                                    <RichTextRenderer content={item.reality} />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );

    const renderImitations = () => (
        <div className="tab-content imitations-tab">
            <div className="critique-intro">
                <p>{critiqueData.alternatives_page.intro}</p>
            </div>
            <div className="imitations-grid">
                {critiqueData.imitations.map((item, index) => (
                    <motion.div
                        key={item.id}
                        className="imitation-card glass-panel"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                    >
                        <div className="imitation-img-wrapper">
                            <img src={item.image} alt={item.title} />
                            <div className="imitation-badge">
                                <AlertTriangle size={16} /> DOES NOT WORK
                            </div>
                        </div>
                        <div className="imitation-content">
                            <h3>{item.title}</h3>
                            <p className="imitation-subtitle">{item.subtitle}</p>
                            <div className="imitation-comparison">
                                <div className="comp-block fear">
                                    <strong>The Pitch:</strong>
                                    <p>{item.claim}</p>
                                </div>
                                <div className="comp-block reality">
                                    <strong>The Reality:</strong>
                                    <p>{item.reality}</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );

    const renderSocialism = () => {
        const renderSection = (section) => {
            switch (section.type) {
                case 'intro-block':
                    return (
                        <div key={section.id} className="ns-section intro-section">
                            <RichTextRenderer content={section.text} className="intro-text" />
                        </div>
                    );
                case 'fear-cards':
                    return (
                        <div key={section.id} className="ns-section fear-section">
                            <h2>{section.title}</h2>
                            <RichTextRenderer content={section.intro} className="section-intro" />
                            <div className="fear-grid">
                                {section.items.map((item, i) => (
                                    <div key={i} className="fear-card glass-panel">
                                        <h3>{item.title}</h3>
                                        <ul>
                                            {item.list.map((li, j) => <li key={j}><RichTextRenderer content={li} /></li>)}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                case 'text-list':
                    return (
                        <div key={section.id} className="ns-section text-list-section">
                            <h2>{section.title}</h2>
                            <div className="content-layout">
                                <div className="text-content">
                                    <RichTextRenderer content={section.intro} className="section-intro" />
                                    <ul className="accent-list">
                                        {section.list.map((li, i) => (
                                            <li key={i}><Check size={20} className="check-icon" /> <RichTextRenderer content={li} /></li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    );
                case 'comparison-block':
                    return (
                        <div key={section.id} className="ns-section comparison-section">
                            <div className="ns-comparison-card glass-panel">
                                <div className="ns-comp-content">
                                    <h3>{section.title}</h3>
                                    <div className="ns-comp-grid">
                                        <div className="comp-block fear">
                                            <h4>The Scare</h4>
                                            <div className="quote"><RichTextRenderer content={`"${section.fear}"`} /></div>
                                            <ul>{section.current_system.map((li, i) => <li key={i}><X size={16} /> <RichTextRenderer content={li} /></li>)}</ul>
                                        </div>
                                        <div className="comp-block reality">
                                            <h4>The Reality</h4>
                                            <div className="quote"><RichTextRenderer content={`"${section.reality}"`} /></div>
                                            <ul>{section.new_system.map((li, i) => <li key={i}><Check size={16} /> <RichTextRenderer content={li} /></li>)}</ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                case 'highlight-block':
                    return (
                        <div key={section.id} className="ns-section highlight-section">
                            <div className="big-quote glass-panel" style={{ textAlign: 'center' }}>
                                <RichTextRenderer content={section.text} />
                            </div>
                        </div>
                    );
                default:
                    return null;
            }
        };

        return (
            <div className="tab-content socialism-tab ns-page">
                <div className="ns-content-container">
                    {socialismData.sections.map(section => renderSection(section))}
                </div>
            </div>
        );
    };

    return (
        <div className="myth-page">
            <SEO
                title="Myth vs Reality - SAFEC+"
                description="Answering common critiques, addressing fake alternatives, and clarifying the socialism question."
                image="/image/critique_hero.jpg"
            />
            <PageHero
                title="Myth vs Reality"
                subtitle="The facts behind the fear"
                image="/image/critique_hero.jpg"
                badge="The Reality Check"
            />

            <div className="container">
                <div className="myth-tabs-nav">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`myth-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <tab.icon size={20} />
                            <span>{tab.label}</span>
                            {activeTab === tab.id && (
                                <motion.div layoutId="tab-underline" className="tab-underline" />
                            )}
                        </button>
                    ))}
                </div>

                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="myth-tab-content-wrapper"
                >
                    {activeTab === 'critiques' && renderCritiques()}
                    {activeTab === 'imitations' && renderImitations()}
                    {activeTab === 'socialism' && renderSocialism()}
                </motion.div>
            </div>
        </div>
    );
}
